import cn from "classnames";
import { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";

import { BaseButton } from "@/src/component/base/BaseButton";
import { ElementInfo } from "@/src/component/partial/__internal__/ElementInspector";
import { ProviderHooks } from "@/src/hook";
import { TrackingLogic } from "@/src/logic";
import { CommonFrontendService } from "@/src/service/common";
import { GrowthBookType } from "@/src/type/growthbook";

import { ElementDetails } from "../ElementDetails";
import { ExperimentDialog } from "../ExperimentDialog";
import { ExperimentDropdown } from "../ExperimentDropdown";
import { ManageVariants } from "../ManageVariants";

import styles from "./VariantTestingPanel.module.scss";

interface VariantTestingPanelProps {
  selectedElement?: HTMLElement | null;
  elementInspectorActive: boolean;
  allActive: boolean;
  growthBook?: GrowthBookType.GrowthBookAndContentVariants;
  allElements: { element: HTMLElement; info: ElementInfo }[];
  setElementInspectorActive: (active: boolean) => void;
  setSelectedElement: (element: HTMLElement) => void;
  setAllActive: (active: boolean) => void;
}

function VariantTestingPanel({
  selectedElement,
  elementInspectorActive,
  allActive,
  growthBook,
  allElements,
  setElementInspectorActive,
  setSelectedElement,
  setAllActive,
}: VariantTestingPanelProps) {
  const toast = ProviderHooks.useToast();
  const [openSaveToVariant, setOpenSaveToVariant] = useState(false);
  const [componentSchemas, setComponentSchemas] = useState<any>(null);
  const [apiSchemas, setApiSchemas] = useState<any>(null);
  const [sectionData, setSectionData] = useState<any>(null);
  const [contentVariant, setContentVariant] = useState<any>(null);
  const htmlComponentId = selectedElement?.getAttribute("component-id");
  const { pageId, dynamicSectionId, sectionId, sectionType, componentId, componentType, pathToElement } =
    TrackingLogic.deconstructComponentId(htmlComponentId);

  function addVariantToExperiment(contentVariant: any) {
    const clone = JSON.parse(JSON.stringify(growthBook?.toolkit ?? []));
    for (const variant of clone ?? []) {
      if (variant.experimentKey === contentVariant.experimentKey) {
        variant.experiments = contentVariant.experiments;
        setContentVariant(variant);
        break;
      }
    }
    setContentVariant(contentVariant);
  }

  function saveToContentVariants() {
    const commonService = CommonFrontendService({ service: "flowhouse-cms", slug: "content-variants" });
    if (contentVariant?.id) {
      //TODO: check that this worked before updating toast success
      commonService.put(contentVariant.id, contentVariant);
    } else {
      //TODO: check that this worked before updating toast success
      commonService.post(contentVariant);
    }
    toast.addMessage({
      title: "Changes saved to content variants",
      type: "success",
    });
    setOpenSaveToVariant(false);
  }

  /**
   * This effect is used to fetch the component schemas and api schemas from the common service
   * These schemas are used to render the forms elements for creating variants
   */
  useEffect(() => {
    (async () => {
      const components: any = await CommonFrontendService({ service: "flowhouse-cms" }).getApiComponents();
      const apiSchemas: any = await CommonFrontendService({ service: "flowhouse-cms" }).getAllApiSchemas();
      if (!components?.response?.payload?.data || !apiSchemas?.response?.payload?.data) {
        return;
      }
      setComponentSchemas(components.response.payload.data);
      const filteredApiSchemas: any = {};
      for (const api of apiSchemas.response.payload.data) {
        if (api.schema.pluralName === "dynamic-pages" || api.schema.pluralName === "dynamic-page-sections") {
          filteredApiSchemas[api.schema.pluralName] = api;
        }
      }
      setApiSchemas(filteredApiSchemas);
    })();
  }, []);

  /**
   * this gets the data for a section that an element is in
   */
  useEffect(() => {
    if (!componentSchemas || !dynamicSectionId || !apiSchemas) {
      return;
    }

    (async () => {
      const params = `query=${encodeURIComponent("populate=deep")}`;
      const section: any = await CommonFrontendService({ service: "flowhouse-cms", slug: "dynamic-page-sections" }).get(dynamicSectionId, params);
      if (!section?.response?.payload) {
        return;
      }
      setSectionData(section.response.payload);
    })();
  }, [pageId, dynamicSectionId, sectionId, sectionType, componentId, componentType, pathToElement, componentSchemas, apiSchemas]);

  if (!growthBook) {
    return (
      <div className={styles.container}>
        <Typography>{"Can't find any experiments for this page"}</Typography>
      </div>
    );
  }

  if (!pageId || !dynamicSectionId || !sectionId || !sectionType || !componentId || !componentType || !pathToElement) {
    return (
      <div className={styles.container}>
        <Typography>Turn on element selector and hover over a component to begin</Typography>
        <div className={styles.defaultButtons}>
          {!elementInspectorActive && (
            <BaseButton
              variant="contained"
              color="primary"
              size="small"
              fullWidth
              onClick={() => {
                setElementInspectorActive(true);
              }}
            >
              Turn On Element Selector
            </BaseButton>
          )}
          {!allActive && (
            <BaseButton
              variant="outlined"
              color="primary"
              fullWidth
              size="small"
              onClick={() => {
                setAllActive(true);
              }}
            >
              Show All Elements
            </BaseButton>
          )}
          {allActive && (
            <>
              {(allElements ?? []).map((element, i) => {
                const { componentId, pathToElement } = TrackingLogic.deconstructComponentId(element.info.componentId);
                return (
                  <Card
                    key={i}
                    variant="outlined"
                    onClick={() => {
                      setSelectedElement(element.element);
                    }}
                  >
                    <CardContent className={cn(styles.contentCard, styles.clickable)}>
                      <ElementDetails
                        htmlComponentId={element.info.componentId}
                        pathToElement={pathToElement}
                        componentId={componentId}
                      />
                    </CardContent>
                  </Card>
                );
              })}
            </>
          )}
        </div>
        <div className={styles.scrollHelper} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ExperimentDropdown growthBook={growthBook} />
      <div className={styles.divider} />
      <div className={styles.titleContainer}>
        <Typography
          variant="h5"
          fontFamily="var(--roughwell)"
          lineHeight="100%"
          className={styles.title}
        >
          Element Details
        </Typography>
      </div>
      <ElementDetails
        htmlComponentId={selectedElement?.getAttribute("component-id") ?? ""}
        pathToElement={pathToElement}
        componentId={componentId}
      />
      <div className={styles.divider} />
      <div className={cn(styles.titleContainer, styles.createVariants)}>
        <Typography
          variant="h5"
          fontFamily="var(--roughwell)"
          lineHeight="100%"
          className={styles.title}
        >
          Variants
        </Typography>
        {/* TODO: add this back in when we have a way to create variants via growthbook */}
        <IconButton
          color="primary"
          sx={{ display: "none" }}
        >
          <AddIcon />
        </IconButton>
      </div>
      <ManageVariants
        htmlComponentId={htmlComponentId}
        componentSchemas={componentSchemas}
        apiSchemas={apiSchemas}
        elementData={sectionData}
        growthBook={growthBook}
        addVariantToExperiment={addVariantToExperiment}
      />
      <BaseButton
        type="submit"
        variant="contained"
        color="primary"
        size="large"
        fullWidth
        onClick={() => setOpenSaveToVariant(true)}
      >
        View All Variants
      </BaseButton>
      <ExperimentDialog
        open={openSaveToVariant}
        onClose={() => setOpenSaveToVariant(false)}
        save={saveToContentVariants}
      />
      <div className={styles.scrollHelper} />
    </div>
  );
}

export default VariantTestingPanel;
